(() => {
  const root = document.querySelector('[data-liver-quiz]');
  if (!root) return;

  const STORAGE_KEY = 'livana-liver-quiz';
  const ADVANCE_DELAY_MS = 230;
  // Taps are ignored this long after a screen renders, so a quick double-tap can't answer the next question.
  const INPUT_GUARD_MS = 300;
  const ANALYZING_MS = 5000;
  const BUILDING_MS = 5800;
  const APPLYING_MS = 3600;
  // Ad click IDs passed through to the product page with the UTMs.
  const CLICK_IDS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid'];
  // Set by the section: product page, bottle shot, mechanism-page images and the verdict's liver-filter animation.
  const { productUrl, productImage, productImageHeight, imageStage, imageFormula, imageDiagram, videoLiver, videoPoster, avatarRay, avatarDana } = root.dataset;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /*
   * The ten questions. Info pages, loaders, verdict, projection and offer are in SCREENS; FLOW sets the order.
   * Step fields:
   *   id         stable key; answers[id] holds the answer
   *   headline   optional large heading above the question; headlineAccent is a word in it shown in the
   *              accent colour; headlineNote is a short line under it
   *   question   heading text
   *   helper     optional line under the heading
   *   smallPrint optional fine print under the answers
   *   whyWeAsk   optional "Why we ask" line under the answers
   *   type       single | multi | image | tiles
   *   required   true blocks Continue until answered (single/image/tiles always need a tap)
   *   options    { value, label, image?, icon?, exclusive? }
   *              value is the fixed internal key; label is visible copy.
   *              icon is the leading emoji (single/tiles) or the image placeholder (image).
   *              exclusive (multi only) clears the other options, and vice versa.
   *   testimonial optional, any type: { quote, name, detail, rating?, image?, verified?, note?, placement? }
   *              (rating 1-5, shown as stars; image is an asset/CDN URL, '' or a broken URL shows an initials circle,
   *              absent shows no avatar; verified adds a badge before detail; note is fine print under
   *              the card). Shown above the heading (placed after it in the DOM), or under the answers with
   *              placement: 'below'. An array of { id, ... } is an A/B test: one is served per session and
   *              its id saved as answers.testimonial_<step id>.
   */
  function getDefaultSteps() {
    return [
      {
        id: 'age',
        headline: "Find Out What's Really Happening With Your Liver",
        headlineAccent: 'Really',
        headlineNote: 'This 2-minute assessment analyzes your symptoms, lifestyle, and risk factors to find a solution.',
        question: 'How old are you?',
        smallPrint: "By continuing, you confirm you're 18 or older and agree to our Terms and Privacy Policy.",
        type: 'single',
        required: true,
        options: [
          { value: '65_plus', label: '65+' },
          { value: '55_64', label: '55–64' },
          { value: '45_54', label: '45–54' },
          { value: '35_44', label: '35–44' },
          { value: 'under_35', label: 'Under 35' },
        ],
        // A/B: one variant per visitor, picked 50/50 and kept for the session (see pickTestimonial).
        testimonial: [
          {
            id: 'ray',
            placement: 'below',
            quote: 'My doctor told me to watch the numbers. For once I watched them go the right way.',
            name: 'Ray C., 58',
            detail: 'Verified Customer',
            verified: true,
            image: avatarRay || '',
            note: '*Individual results vary.',
          },
          {
            id: 'dana',
            placement: 'below',
            quote: 'I was diagnosed with fatty liver at 44. I barely drink. I eat well. I exercise.',
            name: 'Dana W., 46',
            detail: 'Verified Customer',
            verified: true,
            image: avatarDana || '',
            note: '*Individual results vary.',
          },
        ],
      },
      {
        id: 'problem',
        question: "What's your biggest health frustration right now?",
        type: 'single',
        required: true,
        options: [
          { value: 'afternoon_crash', label: 'I hit a wall every afternoon at 2pm', icon: '🪫' },
          { value: 'bloating', label: "I'm bloated by the end of every day", icon: '🎈' },
          { value: 'belly_fat', label: "Belly fat that won't budge, no matter how much weight I lose", icon: '👖' },
          { value: 'brain_fog', label: "Brain fog. I'm not as sharp as I used to be", icon: '🌫️' },
          {
            value: 'bloodwork',
            label: 'My bloodwork came back high, and all I got was “let’s keep an eye on it”',
            icon: '🩸',
          },
          { value: 'hangovers', label: 'Hangovers wreck me after just 2 drinks', icon: '🍷' },
        ],
      },
      {
        id: 'alcohol',
        question: 'Do you drink alcohol, even a few a week?',
        type: 'single',
        required: true,
        options: [
          { value: 'yes', label: 'Yes', icon: '🍷' },
          { value: 'no', label: 'No', icon: '💧' },
        ],
      },
      {
        id: 'energy',
        question: "How's your energy at 2pm compared to when you wake up?",
        type: 'single',
        required: true,
        options: [
          { value: 'drained', label: 'Completely drained: need caffeine or a nap', icon: '🪫' },
          { value: 'lower', label: 'Noticeably lower: the afternoon wall hits hard', icon: '😴' },
          { value: 'slightly', label: 'Slightly tired: still functional but slower', icon: '😐' },
          { value: 'same', label: 'Same energy: no afternoon crash', icon: '⚡' },
        ],
      },
      {
        id: 'onset',
        question: 'When did you start noticing these changes?',
        type: 'single',
        required: true,
        options: [
          { value: 'over_year', label: 'Over a year ago', icon: '🕰️' },
          { value: 'past_year', label: 'In the past year', icon: '📆' },
          { value: 'past_months', label: 'In the past few months', icon: '⏳' },
          { value: 'not_sure', label: 'Not sure', icon: '🤷' },
        ],
      },
      {
        id: 'goal',
        question: 'What results are you expecting from a real fix?',
        type: 'single',
        required: true,
        options: [
          { value: 'energy', label: 'Energy that lasts all day', icon: '⚡' },
          { value: 'flat_stomach', label: 'A flatter stomach', icon: '👖' },
          { value: 'evening_bloat', label: 'No more evening bloat', icon: '🎈' },
          { value: 'focus', label: 'Sharp focus again', icon: '🧠' },
          { value: 'all', label: 'All of the above', icon: '✨' },
        ],
      },
      {
        id: 'body',
        question: 'How would you describe your body right now?',
        type: 'single',
        required: true,
        options: [
          { value: 'lean', label: 'Lean and muscular', icon: '💪' },
          { value: 'average', label: 'Average build', icon: '🧍' },
          { value: 'soft_middle', label: 'Soft around the middle', icon: '👖' },
          { value: 'overweight', label: 'Overweight / out of shape', icon: '⚖️' },
        ],
      },
      {
        id: 'sleep',
        question: 'How many hours of quality sleep do you get per night?',
        type: 'single',
        required: true,
        options: [
          { value: 'under_5', label: 'Less than 5 hours', icon: '😵‍💫' },
          { value: '5_6', label: '5–6 hours', icon: '🥱' },
          { value: '7_8', label: '7–8 hours', icon: '😴' },
          { value: 'over_8', label: 'More than 8 hours', icon: '🛌' },
        ],
      },
      {
        id: 'family',
        question: 'Does liver disease run in your family?',
        whyWeAsk:
          "Fatty liver runs in families, and most people who carry the risk don't know it. Family patterns help us flag yours early.",
        type: 'single',
        required: true,
        options: [
          { value: 'yes', label: 'Yes', icon: '👪' },
          { value: 'no', label: 'No', icon: '🙅' },
          { value: 'not_sure', label: 'Not sure', icon: '🤷' },
        ],
      },
      {
        id: 'processed',
        question: 'How often do you eat processed foods?',
        whyWeAsk:
          'Everything you eat rides one vein straight to your liver before the rest of your body sees it. Processed food keeps that door busy.',
        type: 'single',
        required: true,
        options: [
          { value: 'daily', label: 'Daily: most meals include processed foods', icon: '🍔' },
          { value: 'several_weekly', label: 'Several times a week', icon: '🍕' },
          { value: 'occasionally', label: 'Occasionally: mostly whole foods', icon: '🥗' },
          { value: 'rarely', label: 'Rarely: I avoid processed foods', icon: '🥦' },
        ],
      },
    ];
  }

  // Screen order. The first 13 entries drive the progress bar; the rest follow the last question.
  const FLOW = [
    'age', 'problem', 'alcohol', 'energy', 'onset', 'goal', 'mechanism', 'body', 'sleep', 'analyzing', 'family', 'processed', 'comparison',
    'building', 'verdict', 'projection', 'offer', 'applying',
  ];
  const COUNTED = 13;
  // Back passes over these: they only run on the way forward.
  const AUTO_ADVANCE = ['analyzing', 'building', 'applying'];

  const FDA_FOOTER =
    '†These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease. This quiz is educational and is not medical advice.';

  const LEVELS = ['STABLE', 'EARLY STRAIN', 'STRAINED', 'OVERLOADED'];
  const LEVEL_PINS = [8, 36, 64, 92];
  const LEVEL_STATUS = ['BASELINE SUPPORT', 'STRAIN DETECTED', 'MODERATE STRAIN', 'SEVERE STRAIN'];
  const LEVEL_RELIEF = ['+20%', '+30%', '+45%', '+60%'];

  const CAUSES = {
    front_door: 'FRONT-DOOR LOAD',
    visceral: 'VISCERAL FAT LOAD',
    sluggish: 'SLUGGISH FLOW',
    defense: 'DEFENSE DEPLETION',
    toxic: 'TOXIC LOAD',
    alcohol: 'ALCOHOL LOAD',
    age: 'AGE-RELATED SLOWDOWN',
  };

  // Verdict explanation by primary root cause: [stronger, softer]. Stronger for STRAINED/OVERLOADED.
  const REGISTERS = {
    front_door: [
      'Based on your responses, your liver is getting bombarded through the front door: everything you eat and drink rides one vein straight to it, all day.',
      'Your responses show the deliveries reaching your liver faster than it can process them.',
    ],
    visceral: [
      'Belly fat drains through the same vein directly into your liver, every hour of the day.',
      'Your answers point to fat around the middle working your liver overtime.',
    ],
    sluggish: [
      "Your answers point to a backlog in your liver's drainage system. Bile isn't keeping up with what's coming in, so everything backs up by evening.",
      "Your responses suggest flow through your liver's drain is slower than it should be.",
    ],
    defense: [
      "Your liver's antioxidant defenses are being spent faster than they refill.",
      "Your responses suggest your liver's daily defenses are running low.",
    ],
    alcohol: [
      "Two drinks shouldn't wreck you for a day. Alcohol spends your liver's clean-up crew the same way, every time.",
      "Your responses show alcohol adding to your liver's workload, even in moderation.",
    ],
    fallback: [
      'Your responses point to a liver working harder than it should.',
      'Your responses suggest your liver could use the extra help.',
    ],
  };

  // Fills "we expect you to [goal]" on the projection screen.
  const GOAL_PHRASES = {
    energy: 'have energy that lasts all day',
    flat_stomach: 'have a flatter stomach',
    evening_bloat: 'be done with evening bloat',
    focus: 'have sharp focus again',
    all: 'feel like yourself again',
  };

  const OFFER_HEADLINES = {
    energy: "You'll Get Your Energy Back With Livana Complete Liver Support",
    flat_stomach: "You'll Get Your Flat Stomach Back With Livana Complete Liver Support",
    evening_bloat: "You'll End the Evening Bloat With Livana Complete Liver Support",
    focus: "You'll Get Your Sharp Focus Back With Livana Complete Liver Support",
    all: "You'll Feel Like Yourself Again With Livana Complete Liver Support",
  };

  const SCREENS = {
    mechanism: { type: 'page', render: renderMechanism },
    analyzing: { type: 'loader', render: renderAnalyzing },
    comparison: { type: 'page', render: renderComparison },
    building: {
      type: 'loader',
      render: () =>
        renderLoader({
          title: 'Building your liver profile…',
          body: 'Hold tight while we map your answers against the Livana three-job framework.',
          steps: ['Analyzing your symptoms', 'Cross-referencing your lifestyle', 'Calibrating your plan', 'Personalizing your result'],
          footer: 'Your plan summary is almost ready',
          duration: BUILDING_MS,
          done: next,
        }),
    },
    verdict: { type: 'result', render: renderVerdict },
    projection: { type: 'result', render: renderProjection },
    offer: { type: 'result', render: renderOffer },
    applying: {
      type: 'loader',
      render: () =>
        renderLoader({
          title: 'Applying your discount…',
          body: 'Securing your supply and loading your personalized offer.',
          steps: [
            'Verifying your eligibility',
            'Locking in your supply',
            'Activating your money-back guarantee',
            'Loading your personalized offer',
          ],
          footer: "Please don't refresh. Your discount is being applied",
          duration: APPLYING_MS,
          done: () => window.location.assign(offerUrl()),
        }),
    },
  };

  const steps = getDefaultSteps();
  const stepById = Object.fromEntries(steps.map((step) => [step.id, step]));
  const stepHost = root.querySelector('[data-quiz-step]');
  const backButton = root.querySelector('[data-quiz-back]');
  const progressEl = root.querySelector('[data-quiz-progress]');

  let answers = loadAnswers();
  let current;
  let timers = [];
  let completeTracked = false;
  let inputReadyAt = 0;

  function loadAnswers() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
    } catch {
      return {};
    }
  }

  function saveAnswers() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(answers));
    } catch {
      // Storage can be unavailable (private mode); answers still live in memory.
    }
  }

  function el(tag, attrs = {}, children = []) {
    const node = document.createElement(tag);
    Object.entries(attrs).forEach(([key, value]) => {
      if (value === false || value == null) return;
      if (key === 'text') node.textContent = value;
      else node.setAttribute(key, value === true ? '' : value);
    });
    [].concat(children).forEach((child) => child && node.append(child));
    return node;
  }

  function title(text) {
    return el('h2', { class: 'llq-title', tabindex: '-1', text });
  }

  function nextButton(text, sticky = false) {
    return el('button', { type: 'button', class: `llq-cta${sticky ? ' llq-cta--sticky' : ''}`, 'data-quiz-next': true, text });
  }

  function fdaFooter() {
    return el('p', { class: 'llq-disclaimer', text: FDA_FOOTER });
  }

  function later(fn, ms) {
    timers.push(setTimeout(fn, ms));
  }

  /*
   * The strain profile, mirroring Mars Men v6's reducer (spec "Scoring"). level indexes LEVELS.
   * STABLE is unreachable by design: everyone who finishes has picked a pain, so the floor is EARLY STRAIN.
   */
  function scoreProfile(a) {
    const older = ['55_64', '65_plus'].includes(a.age);
    const drained = a.energy === 'drained';
    const afternoonWall = a.energy === 'lower';
    const drinks = a.alcohol === 'yes';
    const softBody = ['soft_middle', 'overweight'].includes(a.body);
    const count = (signals) => signals.filter(Boolean).length;

    // As on Mars, drinking + drained is a tier 1 signal of its own.
    const t1 = count([drained, a.problem === 'bloodwork', a.problem === 'hangovers', drinks && drained]);
    const t2 = count([softBody, ['afternoon_crash', 'brain_fog', 'bloating'].includes(a.problem), drinks, afternoonWall]);
    const t3 = count([
      ['under_5', '5_6'].includes(a.sleep),
      older,
      a.family === 'yes',
      a.processed === 'daily',
      a.body === 'overweight',
      a.onset === 'over_year',
    ]);
    let level = 1;
    if (t1 >= 3 || (t1 >= 2 && t2 >= 2 && t3 >= 2)) level = 3;
    else if (t1 >= 2 || (t1 >= 1 && t2 >= 2 && t3 >= 2)) level = 2;
    // Each escalation adds one step, capped at OVERLOADED.
    level = Math.min(3, level + count([a.sleep === 'under_5', a.age === '65_plus' && a.onset === 'over_year']));

    let pattern = 'ACUTE';
    if (a.body === 'lean' && ['7_8', 'over_8'].includes(a.sleep)) pattern = 'EARLY ONSET';
    else if (a.onset === 'over_year') pattern = older ? 'PROGRESSIVE' : 'PLATEAUED';

    // Ties keep this order; fewer than two causes are padded with age-related slowdown, then toxic load.
    const weights = {
      front_door: count([['daily', 'several_weekly'].includes(a.processed)]),
      visceral: count([softBody, a.problem === 'belly_fat']),
      sluggish: count([a.problem === 'bloating', afternoonWall]),
      defense: count([a.problem === 'brain_fog', a.sleep === 'under_5']),
      alcohol: count([drinks, a.problem === 'hangovers']),
    };
    const causes = Object.keys(weights)
      .filter((key) => weights[key] > 0)
      .sort((x, y) => weights[y] - weights[x]);
    ['age', 'toxic'].forEach((key) => causes.length < 2 && causes.push(key));

    // Quality of Life Impact: energy and sleep points, plus one for a concern that hits daily life.
    const qol =
      (({ drained: 2, lower: 1 })[a.energy] ?? 0) +
      (({ under_5: 2, '5_6': 1 })[a.sleep] ?? 0) +
      count([['afternoon_crash', 'brain_fog', 'bloodwork', 'hangovers'].includes(a.problem)]);
    const impact = qol >= 4 ? 'HIGH' : qol >= 2 ? 'MEDIUM' : 'EMERGING';

    return { level, pattern, primary: causes[0], secondary: causes[1], impact };
  }

  // GTM forwards the nested quiz object to PostHog, as on the natto quiz.
  function track(event, detail) {
    const index = FLOW.indexOf(current);
    const quiz = {
      quiz_id: 'liver',
      quiz_step: current,
      quiz_step_type: stepById[current]?.type || SCREENS[current].type,
      quiz_step_index: index + 1,
      quiz_step_total: FLOW.length,
      quiz_branch: answers.problem,
    };
    FLOW.slice(0, index).forEach((id) => {
      if (stepById[id] && answers[id] != null) quiz[`answer_${id}`] = answers[id];
    });
    if (answers.testimonial_age) quiz.answer_testimonial_age = answers.testimonial_age;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, quiz: { ...quiz, ...detail } });
  }

  function trackAnswer(step) {
    const values = selectedValues(step);
    const picked = step.options.filter((option) => values.includes(option.value));
    const multi = step.type === 'multi';
    track('question_answered', {
      quiz_answer_key: step.id,
      quiz_answer_value: answers[step.id],
      quiz_answer_label: multi ? picked.map((option) => option.label) : picked[0]?.label,
      quiz_answer_index: multi ? picked.map((option) => step.options.indexOf(option) + 1) : step.options.indexOf(picked[0]) + 1,
    });
  }

  // Strain level + primary root cause + concern, e.g. "overloaded-front_door-bloodwork".
  function persona() {
    const { level, primary } = scoreProfile(answers);
    return [LEVELS[level].toLowerCase().replace(' ', '_'), primary, answers.problem].join('-');
  }

  function offerUrl() {
    const url = new URL(productUrl, window.location.origin);
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (key.startsWith('utm_') || CLICK_IDS.includes(key)) url.searchParams.set(key, value);
    });
    url.searchParams.set('quiz_persona', persona());
    return `${url.pathname}${url.search}`;
  }

  function show(id, direction = 'forward') {
    timers.forEach(clearTimeout);
    timers = [];
    current = id;
    const step = stepById[id];
    const container = step
      ? renderStep(step)
      : el('div', { class: 'llq-step llq-page', 'data-step': id, 'data-type': SCREENS[id].type }, SCREENS[id].render());
    stepHost.replaceChildren(container);
    inputReadyAt = performance.now() + INPUT_GUARD_MS;
    if (step) syncChoices(step);
    updateChrome();
    focusTitle(container);
    track('question_viewed', { quiz_direction: direction });
    // Completion is the verdict view, not the offer click.
    if (id === 'verdict' && !completeTracked) {
      completeTracked = true;
      track('quiz_complete', { quiz_persona: persona() });
    }
    if (id === 'offer') track('offer_viewed');
  }

  // "Question X of 13" and the bar run from screen 2 to screen 13.
  function updateChrome() {
    const index = FLOW.indexOf(current);
    const counted = index > 0 && index < COUNTED;
    progressEl.parentElement.hidden = !counted;
    progressEl.style.inlineSize = `${((index + 1) / COUNTED) * 100}%`;
    backButton.hidden = index === 0 || ['building', 'applying'].includes(current);
  }

  function focusTitle(container) {
    const heading = container.querySelector('.llq-title');
    if (heading) heading.focus({ preventScroll: true });
    window.scrollTo(0, 0);
  }

  function selectedValues(step) {
    return [].concat(answers[step.id] ?? []);
  }

  function setAnswer(step, value) {
    answers[step.id] = value;
    saveAnswers();
  }

  function renderHeadline(step) {
    const [before, after] = step.headlineAccent ? step.headline.split(step.headlineAccent) : [step.headline];
    return el('h2', { class: 'llq-title llq-headline', tabindex: '-1' }, [
      before,
      step.headlineAccent && el('span', { class: 'llq-accent', text: step.headlineAccent }),
      after,
    ]);
  }

  function renderStep(step) {
    const isChoice = ['single', 'multi', 'image', 'tiles'].includes(step.type);
    const testimonial = pickTestimonial(step);
    const above = testimonial && testimonial.placement !== 'below';
    // With a headline, it takes focus and the question becomes a smaller heading; the question keeps the id that labels the options.
    const question = step.headline
      ? el('h3', { class: 'llq-question', id: `llq-q-${step.id}`, text: step.question })
      : el('h2', { class: 'llq-title', tabindex: '-1', id: `llq-q-${step.id}`, text: step.question });
    const body = [
      step.headline && renderHeadline(step),
      step.headlineNote && el('p', { class: 'llq-intro-note', text: step.headlineNote }),
      question,
      // DOM order keeps the focused heading first; CSS lifts the testimonial above it visually.
      ...(above ? renderTestimonial(testimonial) : []),
      step.helper && el('p', { class: 'llq-sub', text: step.helper }),
    ];

    if (isChoice) body.push(renderOptions(step));
    if (step.whyWeAsk) body.push(el('p', { class: 'llq-why' }, [el('strong', { text: 'Why we ask: ' }), step.whyWeAsk]));
    if (step.smallPrint) body.push(el('p', { class: 'llq-small llq-fine', text: step.smallPrint }));
    if (testimonial && !above) body.push(...renderTestimonial(testimonial));
    if (step.type === 'multi') body.push(nextButton('Continue'));

    return el(
      'div',
      { class: `llq-step${above ? ' llq-step--testimonial' : ''}`, 'data-step': step.id, 'data-type': step.type },
      body
    );
  }

  function renderOptions(step) {
    const list = el('div', {
      class: `llq-options llq-options--${step.type}`,
      role: 'group',
      'aria-labelledby': `llq-q-${step.id}`,
    });
    step.options.forEach((option) => {
      const parts = [];
      if (step.type === 'image') {
        parts.push(
          option.image
            ? el('img', { src: option.image, alt: '', loading: 'lazy' })
            : el('span', { class: 'llq-placeholder', 'aria-hidden': 'true', text: option.icon || option.label.charAt(0) })
        );
      }
      if (step.type === 'tiles') {
        parts.push(
          option.image
            ? el('img', { src: option.image, alt: '', loading: 'lazy' })
            : el('span', { class: 'llq-tile-icon', 'aria-hidden': 'true', text: option.icon || option.label.charAt(0) })
        );
      }
      if (step.type === 'single' && option.icon) {
        parts.push(el('span', { class: 'llq-option-icon', 'aria-hidden': 'true', text: option.icon }));
      }
      parts.push(el('span', { class: 'llq-option-label', text: option.label }));
      const button = el('button', { type: 'button', class: 'llq-option', 'aria-pressed': 'false', 'data-value': option.value }, parts);
      button.addEventListener('click', () => choose(step, option));
      list.append(button);
    });
    return list;
  }

  function renderAvatar(image, name) {
    const letters = (name || '').match(/\p{L}+/gu)?.slice(0, 2).map((word) => word[0].toUpperCase()).join('') || '';
    const initials = el('span', { class: 'llq-avatar llq-placeholder', 'aria-hidden': 'true', text: letters });
    if (!image) return initials;
    const photo = el('img', { class: 'llq-avatar', src: image, alt: '', width: '56', height: '56', loading: 'lazy' });
    // A missing or broken photo falls back to the initials circle.
    photo.addEventListener('error', () => photo.replaceWith(initials), { once: true });
    return photo;
  }

  // Picks one variant of an A/B testimonial and saves its id with the answers, so it stays the same for the session.
  function pickTestimonial(step) {
    if (!Array.isArray(step.testimonial)) return step.testimonial;
    const key = `testimonial_${step.id}`;
    let chosen = step.testimonial.find((variant) => variant.id === answers[key]);
    if (!chosen) {
      chosen = step.testimonial[Math.floor(Math.random() * step.testimonial.length)];
      answers[key] = chosen.id;
      saveAnswers();
    }
    return chosen;
  }

  function renderTestimonial({ quote, name, detail, rating, image, verified, note }) {
    const author = [
      name && el('strong', { text: name }),
      detail && el('span', { class: verified ? 'llq-verified' : null, text: detail }),
    ];
    const stars = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    const card = el('figure', { class: 'llq-testimonial' }, [
      stars &&
        el('p', { class: 'llq-stars', role: 'img', 'aria-label': `${stars} out of 5 stars`, text: '★'.repeat(stars) + '☆'.repeat(5 - stars) }),
      quote && el('blockquote', { text: quote }),
      image != null
        ? el('figcaption', { class: 'llq-testimonial-author' }, [
            renderAvatar(image, name),
            el('span', { class: 'llq-testimonial-meta' }, author),
          ])
        : (name || detail) && el('figcaption', {}, author),
    ]);
    return [card, note && el('p', { class: 'llq-small llq-fine llq-testimonial-note', text: note })];
  }

  function renderMechanism() {
    const panels = [
      [
        imageStage,
        'The deliveries pile up',
        'Every meal rides one vein to your liver. Fat builds, flow slows.',
      ],
      [
        imageFormula,
        'The three jobs get covered',
        'Two droppers each morning cover export, defense and flow.',
      ],
      // TODO(art): a dedicated "clear flow" shot; the ingredient diagram stands in.
      [imageDiagram, 'The load comes off', 'Flow runs free again. Energy, digestion and the mirror follow.'],
    ];
    return [
      title("You're not imagining this, and it's not just age."),
      el(
        'ol',
        { class: 'llq-stages' },
        panels.map(([src, heading, line], i) =>
          el('li', {}, [
            el('img', { src, alt: '', width: '640', height: '640', loading: 'lazy' }),
            el('div', {}, [el('strong', {}, [el('span', { text: String(i + 1) }), heading]), el('p', { text: line })]),
          ])
        )
      ),
      el('div', { class: 'llq-text' }, [
        el('p', {
          text: "Your liver filters everything before the rest of your body sees it. That's why you can eat right, exercise, and still feel off. The deliveries keep coming every day.",
        }),
        el('p', {}, el('strong', { text: 'The good news is that this is a load problem, not permanent damage.' })),
        el('p', {}, el('em', { text: 'In a randomized trial, daily N-acetylcysteine lowered ALT by 32.8% in three months.' })),
      ]),
      nextButton('Continue', true),
      fdaFooter(),
    ];
  }

  function renderAnalyzing() {
    const percent = el('span', { class: 'llq-ring-value', text: '0%' });
    const ring = el('div', { class: 'llq-ring', role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-valuenow': '0' }, [
      el('span', { class: 'llq-ring-label', text: 'Analyzing' }),
      percent,
    ]);
    const ticks = 50;
    for (let i = 1; i <= ticks; i += 1) {
      later(() => {
        const value = Math.round((i / ticks) * 100);
        ring.style.setProperty('--progress', value);
        ring.setAttribute('aria-valuenow', value);
        percent.textContent = `${value}%`;
      }, (i / ticks) * ANALYZING_MS);
    }
    later(next, ANALYZING_MS + 150);
    return [
      title('Analyzing your liver profile…'),
      ring,
      el('p', {
        class: 'llq-sub',
        text: "Based on your responses, we're building a comprehensive assessment of your liver health and identifying the key factors affecting your energy and digestion.",
      }),
    ];
  }

  function renderComparison() {
    const rows = [
      'Covers all three jobs: export, defense, flow',
      '13 liver nutrients in one serving',
      'Liquid with LivaSorb, no pills, no fillers',
      'Third-party tested',
      'Rated 4.7★ by 20,000+ buyers',
      '60-day money-back guarantee',
    ];
    return [
      title('Not all liver support is created equal.'),
      el('table', { class: 'llq-compare' }, [
        el(
          'thead',
          {},
          el('tr', {}, [
            el('td'),
            el('th', { scope: 'col', class: 'is-livana', text: 'Complete Liver Support' }),
            el('th', { scope: 'col', text: 'Standard shelf blends' }),
          ])
        ),
        el(
          'tbody',
          {},
          rows.map((row) =>
            el('tr', {}, [
              el('th', { scope: 'row', text: row }),
              el('td', { class: 'is-livana' }, el('span', { class: 'llq-yes', text: 'Yes' })),
              el('td', {}, el('span', { class: 'llq-no', text: 'No' })),
            ])
          )
        ),
      ]),
      el('div', { class: 'llq-text' }, [
        el('p', {
          text: "Milk thistle is the most-studied liver herb there is. But it's one herb doing one job. Your liver has three. Dandelion and artichoke have moved bile for centuries. NAC and glutathione are the antioxidants your liver actually runs on. Most bottles hand you one of these. Livana hands you the system.",
        }),
        el(
          'p',
          {},
          el('em', {
            text: "About 100 million American adults have fat in their liver. More than 95% don't know it. The ones who do something about it judge it by the draw.",
          })
        ),
      ]),
      nextButton('Continue', true),
      fdaFooter(),
    ];
  }

  function renderLoader({ title: heading, body, steps: labels, footer, duration, done }) {
    const items = labels.map((text) => el('li', { text }));
    const stepMs = duration / items.length;
    items.forEach((item, i) =>
      later(() => {
        items[i - 1]?.classList.replace('is-active', 'is-done');
        item.classList.add('is-active');
      }, i * stepMs)
    );
    later(() => {
      items.at(-1).classList.replace('is-active', 'is-done');
      done();
    }, duration);
    return [
      title(heading),
      el('p', { class: 'llq-sub', text: body }),
      el('ol', { class: 'llq-loading-steps' }, items),
      el('p', { class: 'llq-small', text: footer }),
    ];
  }

  function renderVerdict() {
    const { level, pattern, primary, secondary, impact } = scoreProfile(answers);
    const [stronger, softer] = REGISTERS[primary] || REGISTERS.fallback;
    const tiles = [
      ['Strain Pattern:', pattern],
      ['Primary Root Cause:', CAUSES[primary]],
      ['Secondary Root Cause:', CAUSES[secondary]],
      ['Quality of Life Impact:', impact],
    ];
    // The verdict prints as a lab-result slip: level, meter, result rows, then the causes flagged like analytes.
    return [
      el('section', { class: 'llq-slip' }, [
        el('h2', { class: 'llq-title llq-slip-title', tabindex: '-1' }, [
          'Your Liver Assessment: ',
          el('span', { class: 'llq-pill', 'data-level': level, text: LEVELS[level] }),
        ]),
        liverFilterMedia(),
        el('div', { class: 'llq-meter' }, [
          el('p', { class: 'llq-meter-label', text: 'Level of Liver Strain' }),
          el('div', { class: 'llq-meter-track', role: 'img', 'aria-label': `Level of Liver Strain: ${LEVELS[level]}` }, [
            el('span', { class: 'llq-meter-pin', style: `inset-inline-start: ${LEVEL_PINS[level]}%` }),
          ]),
          el(
            'ol',
            { class: 'llq-meter-zones', 'aria-hidden': 'true' },
            ['Low', 'Detected', 'Moderate', 'Severe'].map((zone, i) => el('li', { class: i === level ? 'is-current' : null, text: zone }))
          ),
        ]),
        el('p', { class: 'llq-verdict-text', text: level >= 2 ? stronger : softer }),
        el(
          'dl',
          { class: 'llq-stats' },
          tiles.map(([label, value]) => el('div', {}, [el('dt', { text: label }), el('dd', { text: value })]))
        ),
        el(
          'ul',
          { class: 'llq-chips' },
          Object.entries(CAUSES).map(([key, label]) => {
            const flagged = [primary, secondary].includes(key);
            return el('li', { class: flagged ? 'is-active' : null }, [
              el('span', { text: label }),
              flagged && el('span', { class: 'llq-flag', 'aria-hidden': 'true', text: 'H' }),
              flagged && el('span', { class: 'llq-sr', text: ' (flagged)' }),
            ]);
          })
        ),
      ]),
      nextButton("Show Me What's Possible", true),
    ];
  }

  // The natto quiz's liver-filter loop: plays on arrival, no controls; reduced motion leaves it on its poster.
  function liverFilterMedia() {
    const video = el('video', {
      muted: true,
      loop: true,
      playsinline: true,
      preload: 'metadata',
      poster: videoPoster,
      width: '1440',
      height: '720',
      'aria-label': 'Animation: food passing through the liver filter',
    }, el('source', { src: videoLiver, type: 'video/mp4' }));
    video.muted = true;
    if (!reduceMotion.matches) video.play().catch(() => {});
    return el('div', { class: 'llq-media' }, video);
  }

  // Strain falls 40% over three months, from the verdict level toward zero.
  // The viewBox matches the column width so its 14px labels render at 14px on every screen.
  function strainChart(level, months) {
    const width = Math.round(stepHost.clientWidth) || 320;
    const top = 24;
    const bottom = 168;
    const left = 92;
    const right = width - 8;
    const middle = (top + bottom) / 2;
    const startY = bottom - (bottom - top) * (LEVEL_PINS[level] / 100);
    const endY = bottom - (bottom - startY) * 0.6;
    const ticks = ['TODAY', ...months].map((label, i) => {
      const x = left + ((right - left) * i) / months.length;
      const anchor = i === 0 ? 'start' : i === months.length ? 'end' : 'middle';
      return `<path d="M${x} ${bottom}v4"/><text x="${x}" y="${bottom + 20}" text-anchor="${anchor}">${label}</text>`;
    });
    const figure = el('figure', { class: 'llq-chart', role: 'img', 'aria-label': 'Projected liver strain falling 40% over three months' });
    figure.innerHTML = `<svg viewBox="0 0 ${width} 200" fill="none" aria-hidden="true">
      <path class="llq-chart-grid" d="M${left} ${top}H${right}M${left} ${middle}H${right}M${left} ${bottom}H${right}"/>
      <g class="llq-chart-axis">
        <text x="${left - 8}" y="${top + 4}" text-anchor="end">HIGH</text>
        <text x="${left - 8}" y="${middle + 4}" text-anchor="end">NORMAL</text>
        <text x="${left - 8}" y="${bottom + 4}" text-anchor="end">LOW</text>
        <text transform="translate(14 ${middle}) rotate(-90)" text-anchor="middle">LIVER STRAIN</text>
        ${ticks.join('')}
      </g>
      <path class="llq-chart-line" pathLength="1" d="M${left} ${startY}C${left + 90} ${startY} ${right - 110} ${endY} ${right} ${endY}"/>
      <circle class="llq-chart-dot" cx="${left}" cy="${startY}" r="5"/>
      <text class="llq-chart-tag" x="${left + 8}" y="${startY - 10}">YOU ARE HERE</text>
      <g class="llq-chart-goal">
        <circle class="llq-chart-dot" cx="${right}" cy="${endY}" r="5"/>
        <text class="llq-chart-tag" x="${right}" y="${endY + 22}" text-anchor="end">GOAL · −40% STRAIN</text>
      </g>
    </svg>`;
    return figure;
  }

  function addMonths(date, count) {
    const result = new Date(date);
    const day = result.getDate();
    result.setMonth(result.getMonth() + count);
    if (result.getDate() !== day) result.setDate(0);
    return result;
  }

  function renderProjection() {
    const { level } = scoreProfile(answers);
    const today = new Date();
    const goalDate = addMonths(today, 3);
    const longDate = goalDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const shortDate = goalDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();
    const months = [1, 2, 3].map((n) => addMonths(today, n).toLocaleDateString('en-US', { month: 'short' }).toUpperCase());
    const strip = [
      ['STATUS', LEVEL_STATUS[level]],
      ['PROJECTED RELIEF', LEVEL_RELIEF[level]],
      ['ETA', shortDate],
    ];
    return [
      title("The last solution you'll ever need to feel like yourself again."),
      el('p', { class: 'llq-sub', text: `Based on your answers, we expect you to ${GOAL_PHRASES[answers.goal]} by ${longDate}.` }),
      el('dl', { class: 'llq-strip' }, strip.map(([label, value]) => el('div', {}, [el('dt', { text: label }), el('dd', { text: value })]))),
      strainChart(level, months),
      nextButton('Get My Protocol', true),
    ];
  }

  function renderOffer() {
    const claim = el('button', { type: 'button', class: 'llq-cta llq-cta--sticky', text: 'Claim My Discount' });
    claim.addEventListener('click', () => {
      if (performance.now() < inputReadyAt) return;
      track('offer_cta_clicked', { quiz_persona: persona() });
      next();
    });
    return [
      title(OFFER_HEADLINES[answers.goal]),
      el('p', { class: 'llq-offer-body' }, [
        'Because you qualify, ',
        el('strong', {}, el('u', { text: "we guarantee you'll feel the difference" })),
        ' with our three-job liver system.',
      ]),
      el('p', { class: 'llq-guarantee', text: "If not, you'll receive a full refund. No questions asked." }),
      productImage
        ? el('img', {
            class: 'llq-offer-image',
            src: productImage,
            width: '480',
            height: productImageHeight || null,
            alt: 'Livana Complete Liver Support liquid drops, taken as two droppers daily',
          })
        : el('div', { class: 'llq-offer-image llq-placeholder', 'aria-hidden': 'true', text: 'Product image' }),
      claim,
    ];
  }

  function choose(step, option) {
    if (performance.now() < inputReadyAt) return;
    if (step.type === 'multi') {
      let values = selectedValues(step);
      if (values.includes(option.value)) values = values.filter((value) => value !== option.value);
      else if (option.exclusive) values = [option.value];
      else {
        const exclusive = step.options.filter((item) => item.exclusive).map((item) => item.value);
        values = values.filter((value) => !exclusive.includes(value)).concat(option.value);
      }
      setAnswer(step, values);
      syncChoices(step);
      return;
    }
    setAnswer(step, option.value);
    syncChoices(step);
    trackAnswer(step);
    // Locked until the next screen renders: a second tap during the advance would re-answer and re-track.
    inputReadyAt = Infinity;
    timers.forEach(clearTimeout);
    timers = [];
    later(next, ADVANCE_DELAY_MS);
  }

  function syncChoices(step) {
    const values = selectedValues(step);
    stepHost.querySelectorAll('.llq-option').forEach((button) => {
      button.setAttribute('aria-pressed', values.includes(button.dataset.value));
    });
    const continueButton = stepHost.querySelector('[data-quiz-next]');
    if (continueButton) continueButton.disabled = step.required && values.length === 0;
  }

  function next() {
    const following = FLOW[FLOW.indexOf(current) + 1];
    if (following) show(following);
  }

  function back() {
    let index = FLOW.indexOf(current) - 1;
    while (AUTO_ADVANCE.includes(FLOW[index])) index -= 1;
    if (index >= 0) show(FLOW[index], 'back');
  }

  backButton.addEventListener('click', back);
  stepHost.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quiz-next]');
    if (!button || button.disabled || performance.now() < inputReadyAt) return;
    if (stepById[current]?.type === 'multi') trackAnswer(stepById[current]);
    next();
  });
  show(FLOW[0]);
})();
